import React from "react";
import { ShieldCheck, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

interface SecurePdfViewerProps {
  url: string;
  title: string;
  viewType: "demo" | "full";
}

export const SecurePdfViewer: React.FC<SecurePdfViewerProps> = ({ url, title, viewType }) => {
  // Secure PDF.js Viewer HTML for Web
  const pdfJsHtml = `
    <!DOCTYPE html>
    <html>
    <head>
      <script src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js"></script>
      <style>
        body { margin: 0; background: #1a1a1a; display: flex; flex-direction: column; align-items: center; min-height: 100vh; font-family: system-ui; }
        #canvas-container { width: 100%; max-width: 900px; display: flex; flex-direction: column; align-items: center; padding: 40px 20px; }
        canvas { width: 100%; height: auto; margin-bottom: 30px; box-shadow: 0 20px 50px rgba(0,0,0,0.5); border-radius: 4px; }
        #status { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); color: #666; font-size: 11px; text-transform: uppercase; letter-spacing: 4px; font-weight: 900; }
      </style>
    </head>
    <body>
      <div id="status">Syncing Document Chunks...</div>
      <div id="canvas-container"></div>
      <script>
        const url = '${url}';
        const container = document.getElementById('canvas-container');
        const status = document.getElementById('status');
        
        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
        
        pdfjsLib.getDocument(url).promise.then(pdf => {
          status.style.display = 'none';
          for(let i=1; i<=pdf.numPages; i++) {
            pdf.getPage(i).then(page => {
              const viewport = page.getViewport({ scale: 1.5 });
              const canvas = document.createElement('canvas');
              container.appendChild(canvas);
              const context = canvas.getContext('2d');
              canvas.height = viewport.height;
              canvas.width = viewport.width;
              page.render({ canvasContext: context, viewport: viewport });
            });
          }
        }).catch(err => {
          status.innerText = 'Sync Error: Check Connection';
          console.error(err);
        });
        document.addEventListener('contextmenu', e => e.preventDefault());
      </script>
    </body>
    </html>
  `;

  return (
    <div className="h-full w-full bg-zinc-950 relative overflow-hidden">
      <iframe 
        srcDoc={pdfJsHtml}
        className="h-full w-full border-none shadow-inner"
        title={title}
      />
      
      {/* Premium Status Indicators */}
      <div className="absolute top-8 left-8 z-20 pointer-events-none flex flex-col gap-3">
         <Badge className={cn(
           "px-5 py-2 rounded-2xl shadow-2xl border-none font-black text-[9px] uppercase tracking-[0.3em] backdrop-blur-xl animate-in fade-in slide-in-from-left-4 duration-500",
           viewType === "demo" ? "bg-blue-600/80 text-white" : "bg-emerald-600/80 text-white"
         )}>
            {viewType === "demo" ? "Free Preview Mode" : "Premium Admin Access"}
         </Badge>
         
         <div className="flex items-center gap-3 px-4 py-2 rounded-2xl bg-black/40 backdrop-blur-md border border-white/10 shadow-2xl">
            <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[9px] font-black text-white/60 uppercase tracking-[0.2em]">Encrypted Stream</span>
         </div>
      </div>

      {/* Viewer Metadata Overlay */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
         <div className="flex items-center gap-3 px-6 py-2.5 rounded-2xl bg-zinc-900/80 backdrop-blur-2xl border border-white/5 shadow-2xl">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            <span className="text-[10px] font-black text-white/90 uppercase tracking-[0.2em]">Ayursangrah Neural Viewer v2.0</span>
         </div>
      </div>
    </div>
  );
};
